import { useSearch } from '@tanstack/react-router';
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import {
  ENVIRONMENT_LABELS,
  ENVIRONMENT_TOKENS,
  OPERATIONS,
  type ExecuteResponse,
  type OperationId,
  type PlanResponse,
  type PreflightResponse,
} from '@visualitics-toolkit/shared';

import { Badge, Button, EmptyState } from '../atoms';
import { DurationPair, ErrorBlock, SqlPreview, formatDuration } from '../molecules';
import { ImpactPanel, type ConfirmationState } from '../organisms/ImpactPanel';
import { OperationForm, type FormValues, type ResolvedNames } from '../organisms/OperationForm';
import { ResultTable } from '../organisms/ResultTable';
import { BackupDialog } from '../organisms/BackupDialog';
import { ApiError } from '../../lib/api-client';
import { useActiveConnection, useExecute, useOperations, usePlan, usePreflight } from '../../lib/queries';

/**
 * Layout único das 14 operações (spec/07 §3): executor à esquerda, resultado à direita.
 * O ciclo preflight → impact → plan → execute é o mesmo para todas — nada é especial-cased.
 */
export function OperationLayout({ operationId }: { operationId: OperationId }): ReactNode {
  const definition = OPERATIONS[operationId];
  const connection = useActiveConnection();
  const operations = useOperations(connection?.id);
  const summary = operations.data?.find((entry) => entry.id === operationId);

  // A Biblioteca SQL navega para cá já com o item escolhido (spec/07 §6).
  const search = useSearch({ strict: false }) as { sqlLibraryItemId?: string };
  const [values, setValues] = useState<FormValues>(() => {
    const defaults: FormValues = search.sqlLibraryItemId
      ? { sqlLibraryItemId: search.sqlLibraryItemId }
      : {};
    for (const field of OPERATIONS[operationId].fields) {
      if (field.defaultValue !== undefined && defaults[field.name] === undefined) {
        defaults[field.name] = field.defaultValue;
      }
    }
    return defaults;
  });
  const [resolved, setResolved] = useState<ResolvedNames>({ organizationName: null, userEmail: null });
  const [confirmation, setConfirmation] = useState<ConfirmationState>({
    ackImpact: false,
    typedToken: '',
  });
  const [backupOpen, setBackupOpen] = useState(false);

  const preflight = usePreflight(operationId);
  const plan = usePlan(operationId);
  const execute = useExecute(operationId);

  const preflightData = preflight.data as PreflightResponse | undefined;
  const planData = plan.data as PlanResponse | undefined;
  const executeData = execute.data as ExecuteResponse | undefined;

  const expectedToken = useMemo(() => {
    const kind = preflightData?.impact.typedToken?.kind;
    if (!kind || !connection) return null;
    if (kind === 'environment') return ENVIRONMENT_TOKENS[connection.environment];
    if (kind === 'org-name') return resolved.organizationName;
    if (kind === 'user-email') return resolved.userEmail;
    return null;
  }, [preflightData, connection, resolved]);

  const handleChange = useCallback((name: string, value: unknown) => {
    setValues((current) => ({ ...current, [name]: value }));
    // Mudou parâmetro: o preflight anterior deixa de valer, então a confirmação também.
    setConfirmation({ ackImpact: false, typedToken: '' });
    preflight.reset();
    plan.reset();
    execute.reset();
  }, [preflight, plan, execute]);

  const handleResolvedNames = useCallback((patch: Partial<ResolvedNames>) => {
    setResolved((current) => {
      const next = { ...current, ...patch };
      const same =
        current.organizationName === next.organizationName && current.userEmail === next.userEmail;
      return same ? current : next;
    });
  }, []);

  if (!connection) {
    return (
      <EmptyState
        title="Nenhuma conexão ativa"
        description="Cadastre e ative uma conexão em Configurações antes de executar operações."
      />
    );
  }

  const blockedHere = summary ? !summary.allowedHere : false;
  const gatesOk = isExecuteEnabled(preflightData, confirmation, expectedToken);

  return (
    <div className="grid gap-6 xl:grid-cols-2">
      <section className="space-y-4" aria-label="Executor">
        <header className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-lg font-semibold text-content">{definition.title}</h1>
            {blockedHere && (
              <Badge tone="danger">
                bloqueado em {ENVIRONMENT_LABELS[connection.environment]}
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted">{definition.description}</p>
        </header>

        <div className="card">
          <OperationForm
            fields={definition.fields}
            values={values}
            errors={fieldErrors(preflight.error)}
            onChange={handleChange}
            onResolvedNames={handleResolvedNames}
          />

          <div className="flex flex-wrap gap-2">
            <Button
              variant="primary"
              data-testid="btn-preflight"
              loading={preflight.isPending}
              onClick={() => preflight.mutate(values)}
            >
              Validar (preflight)
            </Button>
            <Button
              data-testid="btn-plan"
              loading={plan.isPending}
              disabled={!preflightData || preflightData.status === 'fail'}
              onClick={() => plan.mutate(values)}
            >
              Planejar
            </Button>
            <Button
              variant="danger"
              data-testid="btn-execute"
              loading={execute.isPending}
              disabled={!gatesOk || blockedHere}
              onClick={() =>
                execute.mutate({
                  params: values,
                  confirmation: {
                    ackImpact: confirmation.ackImpact,
                    typedToken: confirmation.typedToken || undefined,
                  },
                })
              }
            >
              Executar
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setValues({});
                setConfirmation({ ackImpact: false, typedToken: '' });
                preflight.reset();
                plan.reset();
                execute.reset();
              }}
            >
              Cancelar
            </Button>
          </div>
        </div>

        {preflight.error instanceof Error && <ErrorBlock message={preflight.error.message} />}

        {preflightData && (
          <>
            <div className="card space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-medium text-content">
                  Preflight — {preflightData.status.toUpperCase()}
                </h2>
                <span className="font-mono text-xs text-muted">
                  {formatDuration(preflightData.preflightMs)}
                </span>
              </div>

              {preflightData.errors.map((error) => (
                <ErrorBlock key={error} message={error} />
              ))}
              {preflightData.warnings.map((warning) => (
                <p key={warning} className="text-sm text-severity-destructive">
                  {warning}
                </p>
              ))}

              {preflightData.sql.map((statement) => (
                <SqlPreview
                  key={statement.label}
                  label={statement.label}
                  sql={statement.sql}
                  params={statement.params}
                />
              ))}

              {preflightData.counts.length > 0 && (
                <ResultTable
                  table={{
                    label: 'Contagens do preflight',
                    columns: ['schema', 'table', 'keyColumn', 'rowCount'],
                    rows: preflightData.counts as unknown as Record<string, unknown>[],
                    rowCount: preflightData.counts.length,
                  }}
                />
              )}
            </div>

            <ImpactPanel
              impact={preflightData.impact}
              expectedToken={expectedToken}
              confirmation={confirmation}
              onConfirmationChange={setConfirmation}
              onRequestBackup={() => setBackupOpen(true)}
            />
          </>
        )}

        {planData && (
          <div className="card space-y-2">
            <h2 className="text-sm font-medium text-content">Plano</h2>
            <p className="text-sm text-muted">
              custo EXPLAIN: {planData.estimatedCost ?? '—'} · linhas estimadas:{' '}
              {planData.estimatedRows ?? '—'} · p50 {formatDuration(planData.estimatedDurationMs.p50)}{' '}
              / p95 {formatDuration(planData.estimatedDurationMs.p95)} (
              {planData.estimatedDurationMs.source}, n={planData.estimatedDurationMs.sampleSize})
            </p>
            {planData.notes.map((note) => (
              <p key={note} className="text-xs text-muted">
                {note}
              </p>
            ))}
          </div>
        )}
      </section>

      <section className="space-y-4" aria-label="Resultado" aria-live="polite">
        {execute.error instanceof Error && <ExecuteError error={execute.error} />}

        {executeData ? (
          <>
            <div className="card flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Badge tone={executeData.status === 'ok' ? 'success' : 'danger'}>
                  {executeData.status}
                </Badge>
                <span className="font-mono text-xs text-muted">run {executeData.runId}</span>
              </div>
              <DurationPair
                estimatedMs={executeData.estimatedDurationMs?.p50}
                actualMs={executeData.executeMs}
              />
            </div>

            {executeData.result.messages.length > 0 && (
              <div className="card space-y-1 text-sm text-content">
                {executeData.result.messages.map((message) => (
                  <p key={message}>{message}</p>
                ))}
              </div>
            )}

            {executeData.artifacts.length > 0 && (
              <div className="card space-y-2">
                <h2 className="text-sm font-medium text-content">Artefatos</h2>
                {executeData.artifacts.map((artifact) => (
                  <a
                    key={artifact.id}
                    href={`/api/v1/artifacts/${artifact.id}/download`}
                    className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm hover:border-accent"
                  >
                    <span className="font-mono text-xs">{artifact.filename}</span>
                    <span className="text-muted">{(artifact.bytes / 1024).toFixed(1)} KiB</span>
                  </a>
                ))}
              </div>
            )}

            {executeData.result.tables.map((table) => (
              <div className="card" key={table.label}>
                <ResultTable table={table} />
              </div>
            ))}
          </>
        ) : (
          <EmptyState
            title="Nenhum resultado ainda"
            description="Valide o preflight, revise o impacto e execute para ver o resultado aqui."
          />
        )}
      </section>

      {backupOpen && (
        <BackupDialog
          connection={connection}
          organizationId={typeof values.organizationId === 'string' ? values.organizationId : undefined}
          onClose={() => setBackupOpen(false)}
        />
      )}
    </div>
  );
}

function ExecuteError({ error }: { error: Error }): ReactNode {
  if (error instanceof ApiError && error.code === 'BACKUP_REQUIRED') {
    return (
      <ErrorBlock message={`${error.message} Faça um backup pelo painel de impacto e tente de novo.`} />
    );
  }
  return <ErrorBlock message={error.message} />;
}

/** Espelha os gates do servidor para não deixar o operador clicar num 403 previsível. */
function isExecuteEnabled(
  preflight: PreflightResponse | undefined,
  confirmation: ConfirmationState,
  expectedToken: string | null,
): boolean {
  if (!preflight || preflight.status === 'fail') return false;
  if (preflight.impact.backup.requiredWithinHours !== null && !preflight.impact.backup.satisfied) {
    return false;
  }
  if (!preflight.impact.requiresConfirmation) return true;
  if (!confirmation.ackImpact) return false;
  if (!preflight.impact.typedToken) return true;
  return Boolean(expectedToken) && confirmation.typedToken.trim() === expectedToken?.trim();
}

function fieldErrors(error: unknown): Record<string, string> {
  if (!(error instanceof ApiError) || error.code !== 'INVALID_PARAMS') return {};
  const issues = error.details?.issues;
  if (!Array.isArray(issues)) return {};

  const map: Record<string, string> = {};
  for (const issue of issues as { path?: string; message?: string }[]) {
    if (issue.path && issue.message) map[issue.path] = issue.message;
  }
  return map;
}
